import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
} from 'react';

import { type MyOCData } from '~/types/myoc';
import { type MyOCConfig } from '~/utils/myoc.server';

export type MyOCProviderProps = {
	myocConfig: MyOCConfig;
	myocData: MyOCData;
	submit: (url: string) => void;
};

export type MyOCProviderData = {
	invalidateMyOCUser: () => void;
	logoutMyOCUser: () => void;
	myocConfig: MyOCConfig;
	myocData: MyOCData;
};

export const MyOCContext = createContext<MyOCProviderData>(
	undefined as any as MyOCProviderData,
);
MyOCContext.displayName = 'MyOCContext';

export const MyOCProvider = ({
	children,
	myocConfig,
	myocData,
	submit,
	...props
}: PropsWithChildren<MyOCProviderProps>) => {
	const invalidateMyOCUser = useCallback(() => {
		submit('/api/cx/myoc/invalidate');
	}, [submit]);

	const logoutMyOCUser = useCallback(() => {
		submit('/api/cx/myoc/logout');
	}, [submit]);

	const value = useMemo(
		() => ({
			myocConfig,
			myocData,
			invalidateMyOCUser,
			logoutMyOCUser,
		}),
		[myocConfig, myocData, invalidateMyOCUser, logoutMyOCUser],
	);

	return (
		<MyOCContext.Provider value={value} {...props}>
			{children}
		</MyOCContext.Provider>
	);
};

export const useMyOCContext = () => {
	const context = useContext(MyOCContext);
	if (!context) {
		throw new Error('useMyOCContext can only be used inside of MyOCProvider');
	}

	return context;
};
